import React, { useState, useEffect } from 'react';
import { X, Upload } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/Drawer.css';
import '../styles/DrawerForm.css';
import ImageEditor from './ImageEditor';
import RelatedDeficiencyModal from './RelatedDeficiencyModal';

interface RelatedDeficiencyItem {
  deficiency: string;
  relationshipType: 'duplicate' | 'related';
  deficiencyNumber?: number;
  title?: string;
}

interface DeficiencyFormData {
  projectId: string;
  scopeType: 'units' | 'commonArea';
  unitId?: string;
  commonAreaId?: string;
  inspectionType?: string;
  typeOfDeficiency?: string;
  locationCategory?: string;
  impactLevel: string;
  priority: string;
  dueDate: string;
  tags: string;
  title: string;
  description: string;
  attachments: File[];
  isPrivate: boolean;
  scheduleImpact?: string;
  chargeback?: string;
  costImpact?: string;
  trade?: string;
  approver?: string;
  costCodes?: string;
  privateNotes?: string;
  relatedDeficiencyItems: RelatedDeficiencyItem[];
}

interface Unit {
  _id: string;
  unitNo: string;
}

interface CommonArea {
  _id: string;
  areaName: string;
  level: string;
}

interface InspectionType {
  _id: string;
  name: string;
}

interface Location {
  id: string;
  name: string;
}

interface DeficiencyType {
  id: string;
  name: string;
}

interface Priority {
  _id: string;
  name: string;
}

interface Trade {
  _id: string;
  name: string;
}

interface Manager {
  contactId: string;
  name: string;
  email: string;
  manager: boolean;
}

interface Tag {
  _id: string;
  name: string;
}

interface Project {
  _id: string;
  name: string;
}

interface DeficiencyFormSliderProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: DeficiencyFormData) => void;
  formData: DeficiencyFormData;
  setFormData: React.Dispatch<React.SetStateAction<DeficiencyFormData>>;
  workspaceId: string;
  projects: Project[];
  saving?: boolean;
}

const DeficiencyFormSlider: React.FC<DeficiencyFormSliderProps> = ({
  isOpen,
  onClose,
  onSave,
  formData,
  setFormData,
  workspaceId,
  projects,
  saving = false
}) => {
  const [attachmentPreviews, setAttachmentPreviews] = useState<string[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [units, setUnits] = useState<Unit[]>([]);
  const [filteredUnits, setFilteredUnits] = useState<Unit[]>([]);
  const [unitSearchQuery, setUnitSearchQuery] = useState('');
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [levels, setLevels] = useState<string[]>([]);
  const [commonAreas, setCommonAreas] = useState<CommonArea[]>([]);
  const [filteredCommonAreas, setFilteredCommonAreas] = useState<CommonArea[]>([]);
  const [selectedLevel, setSelectedLevel] = useState('');
  const [loadingLevels, setLoadingLevels] = useState(false);
  const [loadingCommonAreas, setLoadingCommonAreas] = useState(false);
  const [inspectionTypes, setInspectionTypes] = useState<InspectionType[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [deficiencyTypes, setDeficiencyTypes] = useState<DeficiencyType[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingInspectionTypes, setLoadingInspectionTypes] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingDeficiencyTypes, setLoadingDeficiencyTypes] = useState(false);
  const [loadingPriorities, setLoadingPriorities] = useState(false);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [loadingTrades, setLoadingTrades] = useState(false);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [filteredTags, setFilteredTags] = useState<Tag[]>([]);
  const [imageEditorOpen, setImageEditorOpen] = useState(false);
  const [currentImageFile, setCurrentImageFile] = useState<File | null>(null);
  const [showRelatedDeficiencyModal, setShowRelatedDeficiencyModal] = useState(false);

  // Fetch service settings data when project changes OR when slider opens with existing project
  useEffect(() => {
    if (formData.projectId && isOpen) {
      fetchInspectionTypes(formData.projectId);
      fetchLocations(formData.projectId);
      fetchDeficiencyTypes(formData.projectId);
      fetchPriorities(formData.projectId);
      fetchTrades(formData.projectId);
      fetchManagers(formData.projectId);
      fetchTags(formData.projectId);

      // Fetch levels if scope is commonArea
      if (formData.scopeType === 'commonArea') {
        fetchLevels(formData.projectId);
      }
    } else if (!formData.projectId) {
      setInspectionTypes([]);
      setLocations([]);
      setDeficiencyTypes([]);
      setPriorities([]);
      setTrades([]);
      setManagers([]);
      setAvailableTags([]);
      setLevels([]);
      setCommonAreas([]);
      setFilteredCommonAreas([]);
    }
  }, [formData.projectId, formData.scopeType, isOpen]);

  // Fetch units when user searches
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (unitSearchQuery.trim().length >= 1 && formData.projectId && showUnitDropdown) {
        fetchUnits(formData.projectId, unitSearchQuery);
      } else if (unitSearchQuery.trim().length === 0) {
        setUnits([]);
        setFilteredUnits([]);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(delayDebounce);
  }, [unitSearchQuery, formData.projectId, showUnitDropdown]);

  const fetchUnits = async (projectId: string, searchQuery: string) => {
    if (!workspaceId || !projectId) return;

    try {
      setLoadingUnits(true);
      const response = await api.get(`/workspaces/${workspaceId}/units`, {
        params: {
          projectId: projectId,
          search: searchQuery,
          limit: 50,
          sortBy: 'unitNo',
          sortOrder: 'asc'
        }
      });
      const unitsList = response.data.units || [];
      setUnits(unitsList);
      setFilteredUnits(unitsList);
    } catch (err) {
      console.error('Error fetching units:', err);
      toast.error('Failed to load units');
      setUnits([]);
      setFilteredUnits([]);
    } finally {
      setLoadingUnits(false);
    }
  };

  const fetchInspectionTypes = async (projectId: string) => {
    try {
      setLoadingInspectionTypes(true);
      const response = await api.get(`/service-settings/${projectId}/inspection-types`);
      console.log('Inspection Types Response:', response.data);

      // Response structure: { items: [...] }
      const typesData = response.data.items || [];
      const types = typesData.map((type: any) => ({
        _id: type._id,
        name: type.name
      }));

      console.log('Mapped Inspection Types:', types);
      setInspectionTypes(types);
    } catch (err) {
      console.error('Error fetching inspection types:', err);
      toast.error('Failed to load inspection types');
    } finally {
      setLoadingInspectionTypes(false);
    }
  };

  const fetchLocations = async (projectId: string) => {
    try {
      setLoadingLocations(true);
      const response = await api.get(`/service-settings/${projectId}/locations`);
      console.log('Locations Response:', response.data);

      // Response structure: { locations: [...] }
      const locsData = response.data.locations || [];
      const locs = locsData.map((loc: any) => ({
        id: loc._id,
        name: loc.name
      }));

      console.log('Mapped Locations:', locs);
      setLocations(locs);
    } catch (err) {
      console.error('Error fetching locations:', err);
      toast.error('Failed to load locations');
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchDeficiencyTypes = async (projectId: string) => {
    try {
      setLoadingDeficiencyTypes(true);
      const response = await api.get(`/service-settings/${projectId}/deficiency-types`);
      console.log('Deficiency Types Response:', response.data);

      // Response structure: { items: [...] }
      const typesData = response.data.items || [];
      const types = typesData.map((type: any) => ({
        id: type._id,
        name: type.name
      }));

      console.log('Mapped Deficiency Types:', types);
      setDeficiencyTypes(types);
    } catch (err) {
      console.error('Error fetching deficiency types:', err);
      toast.error('Failed to load deficiency types');
    } finally {
      setLoadingDeficiencyTypes(false);
    }
  };

  const fetchPriorities = async (projectId: string) => {
    try {
      setLoadingPriorities(true);
      const response = await api.get(`/service-settings/${projectId}/priorities`);
      console.log('Priorities Response:', response.data);

      // Response structure: { items: [...] }
      const prioritiesData = response.data.items || [];
      const prioritiesList = prioritiesData.map((priority: any) => ({
        _id: priority._id,
        name: priority.name
      }));

      console.log('Mapped Priorities:', prioritiesList);
      setPriorities(prioritiesList);
    } catch (err) {
      console.error('Error fetching priorities:', err);
      toast.error('Failed to load priorities');
    } finally {
      setLoadingPriorities(false);
    }
  };

  const fetchTrades = async (projectId: string) => {
    try {
      setLoadingTrades(true);
      const response = await api.get(`/service-settings/${projectId}/trades`);
      console.log('Trades Response:', response.data);

      // Response structure: { items: [...] }
      const tradesData = response.data.items || [];
      const tradesList = tradesData.map((trade: any) => ({
        _id: trade._id,
        name: trade.name
      }));

      console.log('Mapped Trades:', tradesList);
      setTrades(tradesList);
    } catch (err) {
      console.error('Error fetching trades:', err);
      toast.error('Failed to load trades');
    } finally {
      setLoadingTrades(false);
    }
  };

  const fetchManagers = async (projectId: string) => {
    try {
      setLoadingManagers(true);
      const response = await api.get(`/service-settings/${projectId}/users`);
      console.log('Managers Response:', response.data);

      // Response structure: { users: [...] }
      const usersData = response.data.users || [];

      // Filter only users with manager role
      const managersList = usersData
        .filter((user: any) => user.manager === true)
        .map((manager: any) => ({
          contactId: manager.contactId,
          name: manager.name,
          email: manager.email,
          manager: manager.manager
        }));

      console.log('Mapped Managers:', managersList);
      setManagers(managersList);
    } catch (err) {
      console.error('Error fetching managers:', err);
      toast.error('Failed to load managers');
    } finally {
      setLoadingManagers(false);
    }
  };

  const fetchTags = async (projectId: string) => {
    try {
      setLoadingTags(true);
      const response = await api.get(`/service-settings/${projectId}/tags`);
      console.log('Tags Response:', response.data);

      // Response structure: { items: [...] } or { tags: [...] }
      const tagsData = response.data.items || response.data.tags || [];
      const tagsList = tagsData.map((tag: any) => ({
        _id: tag._id,
        name: tag.name
      }));

      console.log('Mapped Tags:', tagsList);
      setAvailableTags(tagsList);
    } catch (err) {
      console.error('Error fetching tags:', err);
      toast.error('Failed to load tags');
    } finally {
      setLoadingTags(false);
    }
  };

  const addNewTag = async (tagName: string) => {
    if (!formData.projectId) return;

    try {
      const response = await api.post(`/service-settings/${formData.projectId}/tags`, {
        name: tagName
      });

      const newTag = {
        _id: response.data._id || response.data.tag?._id,
        name: tagName
      };

      setAvailableTags([...availableTags, newTag]);
      toast.success('Tag added to service settings');
      return newTag;
    } catch (err) {
      console.error('Error adding new tag:', err);
      toast.error('Failed to add tag');
      return null;
    }
  };

  const fetchLevels = async (projectId: string) => {
    if (!workspaceId || !projectId) return;

    try {
      setLoadingLevels(true);
      const response = await api.get(`/workspaces/${workspaceId}/common-areas/by-project/${projectId}/levels`);
      console.log('Levels Response:', response.data);

      const levelsList = response.data.levels || [];
      setLevels(levelsList);
    } catch (err) {
      console.error('Error fetching levels:', err);
      toast.error('Failed to load levels');
      setLevels([]);
    } finally {
      setLoadingLevels(false);
    }
  };

  const fetchCommonAreasByLevel = async (projectId: string, level: string) => {
    if (!workspaceId || !projectId || !level) return;

    try {
      setLoadingCommonAreas(true);
      const response = await api.get(`/workspaces/${workspaceId}/common-areas`, {
        params: {
          projectId: projectId,
          level: level,
          status: 'ACTIVE',
          limit: 100,
          sortBy: 'areaName',
          sortOrder: 'asc'
        }
      });
      console.log('Common Areas Response:', response.data);

      const areasData = response.data.commonAreas || [];
      setCommonAreas(areasData);
      setFilteredCommonAreas(areasData);
    } catch (err) {
      console.error('Error fetching common areas:', err);
      toast.error('Failed to load common areas');
      setCommonAreas([]);
      setFilteredCommonAreas([]);
    } finally {
      setLoadingCommonAreas(false);
    }
  };

  // Filter tags based on input
  useEffect(() => {
    if (tagInput.trim()) {
      const filtered = availableTags.filter(tag =>
        tag.name.toLowerCase().includes(tagInput.toLowerCase()) &&
        !selectedTags.includes(tag.name)
      );
      setFilteredTags(filtered);
    } else {
      setFilteredTags([]);
    }
  }, [tagInput, availableTags, selectedTags]);

  // Initialize selectedTags from formData when slider opens
  useEffect(() => {
    if (isOpen && formData.tags) {
      const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
      setSelectedTags(tagsArray);
    } else if (!isOpen) {
      setSelectedTags([]);
      setTagInput('');
    }
  }, [isOpen, formData.tags]);

  // Prevent body scroll when slider is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      // Check if file is an image
      if (!file.type.startsWith('image/')) {
        toast.error('Only image files are allowed');
        return;
      }

      // Open image editor with the selected file
      setCurrentImageFile(file);
      setImageEditorOpen(true);

      // Reset the input value so the same file can be selected again
      e.target.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);

    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }

    setCurrentImageFile(file);
    setImageEditorOpen(true);
  };

  const handleImageEditorSave = (editedFile: File) => {
    // Add the edited image to attachments
    setFormData({ ...formData, attachments: [...formData.attachments, editedFile] });

    // Create preview for the image
    const reader = new FileReader();
    reader.onloadend = () => {
      setAttachmentPreviews(prev => [...prev, reader.result as string]);
    };
    reader.readAsDataURL(editedFile);

    // Close the image editor
    setImageEditorOpen(false);
    setCurrentImageFile(null);

    toast.success('Image added successfully');
  };

  const removeAttachment = (index: number) => {
    const newAttachments = formData.attachments.filter((_, i) => i !== index);
    const newPreviews = attachmentPreviews.filter((_, i) => i !== index);
    setFormData({ ...formData, attachments: newAttachments });
    setAttachmentPreviews(newPreviews);
  };

  const handleSaveRelatedDeficiencies = (selectedItems: RelatedDeficiencyItem[]) => {
    setFormData({
      ...formData,
      relatedDeficiencyItems: [...formData.relatedDeficiencyItems, ...selectedItems]
    });
    const duplicateCount = selectedItems.filter(item => item.relationshipType === 'duplicate').length;
    const relatedCount = selectedItems.filter(item => item.relationshipType === 'related').length;

    let message = '';
    if (duplicateCount > 0 && relatedCount > 0) {
      message = `${duplicateCount} duplicate and ${relatedCount} related deficienc${relatedCount === 1 ? 'y' : 'ies'} added`;
    } else if (duplicateCount > 0) {
      message = `${duplicateCount} duplicate deficienc${duplicateCount === 1 ? 'y' : 'ies'} added`;
    } else {
      message = `${relatedCount} related deficienc${relatedCount === 1 ? 'y' : 'ies'} added`;
    }
    toast.success(message);
  };

  const handleRemoveRelatedDeficiency = (index: number) => {
    setFormData({
      ...formData,
      relatedDeficiencyItems: formData.relatedDeficiencyItems.filter((_, i) => i !== index)
    });
  };

  const validateForm = (): boolean => {
    // Check Project
    if (!formData.projectId) {
      toast.error('Please select a project');
      return false;
    }

    // Check Scope Type
    if (!formData.scopeType) {
      toast.error('Please select a scope (Units or Common Area)');
      return false;
    }

    // Validate based on scope type
    if (formData.scopeType === 'units') {
      // Check Unit
      if (!formData.unitId) {
        toast.error('Please select a unit');
        return false;
      }

      // Check Inspection Type
      if (!formData.inspectionType) {
        toast.error('Please select an inspection type');
        return false;
      }

      // Check Impact Level
      if (!formData.impactLevel) {
        toast.error('Please select an impact level');
        return false;
      }

      // Check Location/Category
      if (!formData.locationCategory) {
        toast.error('Please select a location/category');
        return false;
      }

      // Check Type of Deficiency
      if (!formData.typeOfDeficiency) {
        toast.error('Please select a type of deficiency');
        return false;
      }

      // Check Priority
      if (!formData.priority) {
        toast.error('Please select a priority');
        return false;
      }

      // Check Due Date
      if (!formData.dueDate) {
        toast.error('Please select a due date');
        return false;
      }
    } else if (formData.scopeType === 'commonArea') {
      // Check Level
      if (!selectedLevel) {
        toast.error('Please select a level');
        return false;
      }

      // Check Common Area
      if (!formData.commonAreaId) {
        toast.error('Please select an area');
        return false;
      }

      // Check Inspection Type
      if (!formData.inspectionType) {
        toast.error('Please select an inspection type');
        return false;
      }

      // Check Impact Level
      if (!formData.impactLevel) {
        toast.error('Please select an impact level');
        return false;
      }

      // Check Location/Category
      if (!formData.locationCategory) {
        toast.error('Please select a location/category');
        return false;
      }

      // Check Type of Deficiency
      if (!formData.typeOfDeficiency) {
        toast.error('Please select a type of deficiency');
        return false;
      }

      // Check Priority
      if (!formData.priority) {
        toast.error('Please select a priority');
        return false;
      }

      // Check Due Date
      if (!formData.dueDate) {
        toast.error('Please select a due date');
        return false;
      }
    }

    // Check Title (common for both scopes)
    if (!formData.title || !formData.title.trim()) {
      toast.error('Please enter a title');
      return false;
    }

    // Check Trade (common for both scopes)
    if (!formData.trade) {
      toast.error('Please select a trade');
      return false;
    }

    return true;
  };

  const handleSave = () => {
    // Validate form before calling onSave
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <>
      {/* Drawer Overlay */}
      <div className="drawer-overlay" />

      {/* Drawer Container */}
      <div className="drawer">
        {/* Drawer Header */}
        <div className="drawer-header">
          <h2 className="drawer-title">New Deficiency</h2>
          <button className="drawer-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Drawer Content */}
        <div className="drawer-content" style={{ padding: '16px' }}>
          <div className="drawer-form-content">
            {/* Project and Scope in same row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Project *</label>
                <select
                  className="form-input"
                  value={formData.projectId}
                  onChange={(e) => {
                    setFormData({ ...formData, projectId: e.target.value, unitId: '' });
                    setUnitSearchQuery('');
                  }}
                  required
                >
                  <option value="">Select a project</option>
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: 0, gridColumn: 'span 2' }}>
                <label className="form-label">Scope *</label>
                <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="scopeType"
                      value="units"
                      checked={formData.scopeType === 'units'}
                      onChange={(e) => {
                        setFormData({ ...formData, scopeType: 'units', commonAreaId: '' });
                        setSelectedLevel('');
                        setCommonAreas([]);
                        setFilteredCommonAreas([]);
                      }}
                    />
                    <span style={{ fontSize: '14px' }}>Units</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="scopeType"
                      value="commonArea"
                      checked={formData.scopeType === 'commonArea'}
                      onChange={(e) => {
                        setFormData({ ...formData, scopeType: 'commonArea', unitId: '' });
                        setUnitSearchQuery('');
                        if (formData.projectId) {
                          fetchLevels(formData.projectId);
                        }
                      }}
                    />
                    <span style={{ fontSize: '14px' }}>Common Area</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Unit Selection (only if Units is selected) with Inspection Type and Status */}
            {formData.scopeType === 'units' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Unit *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      className="form-input"
                      value={unitSearchQuery}
                      onChange={(e) => {
                        setUnitSearchQuery(e.target.value);
                        setShowUnitDropdown(true);
                        if (!e.target.value) {
                          setFormData({ ...formData, unitId: '' });
                        }
                      }}
                      onFocus={() => {
                        if (unitSearchQuery && filteredUnits.length > 0) {
                          setShowUnitDropdown(true);
                        }
                      }}
                      onBlur={() => {
                        // Delay to allow click on dropdown items
                        setTimeout(() => setShowUnitDropdown(false), 200);
                      }}
                      placeholder={!formData.projectId ? "Please select a project first" : "Search unit by number"}
                      disabled={!formData.projectId}
                      required
                    />
                    {loadingUnits && (
                      <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                        <div className="projects-loading-spinner" style={{ width: '16px', height: '16px' }} />
                      </div>
                    )}
                    {showUnitDropdown && unitSearchQuery && filteredUnits.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        maxHeight: '200px',
                        overflowY: 'auto',
                        backgroundColor: 'white',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        marginTop: '4px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000
                      }}>
                        {filteredUnits.map((unit) => (
                          <div
                            key={unit._id}
                            onClick={() => {
                              setFormData({ ...formData, unitId: unit._id });
                              setUnitSearchQuery(unit.unitNo);
                              setShowUnitDropdown(false);
                              setFilteredUnits([]);
                            }}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #F3F4F6',
                              backgroundColor: formData.unitId === unit._id ? '#F9FAFB' : 'white',
                              transition: 'background-color 0.15s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                            onMouseLeave={(e) => {
                              if (formData.unitId !== unit._id) {
                                e.currentTarget.style.backgroundColor = 'white';
                              }
                            }}
                          >
                            {unit.unitNo}
                          </div>
                        ))}
                      </div>
                    )}
                    {showUnitDropdown && unitSearchQuery && !loadingUnits && filteredUnits.length === 0 && !formData.unitId && (
                      <small style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: '#6B7280' }}>
                        No units found for "{unitSearchQuery}"
                      </small>
                    )}
                  </div>
                </div>

                {/* Inspection Type */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Inspection Type *</label>
                  <select
                    className="form-input"
                    value={formData.inspectionType}
                    onChange={(e) => setFormData({ ...formData, inspectionType: e.target.value })}
                  >
                    <option value="">Select Inspection Type</option>
                    {inspectionTypes.map((type) => (
                      <option key={type._id} value={type._id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Status *</label>
                  <select
                    className="form-input"
                    defaultValue="open"
                  >
                    <option value="open">Open</option>
                    <option value="assigned">Assigned</option>
                    <option value="accepted">Accepted</option>
                    <option value="not-accepted">Not Accepted</option>
                    <option value="completed">Completed</option>
                    <option value="closed">Closed</option>
                    <option value="reopened">Reopened</option>
                  </select>
                </div>
              </div>
            )}

            {/* Common Area Selection (only if Common Area is selected) */}
            {formData.scopeType === 'commonArea' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                  {/* Level Dropdown */}
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Level *</label>
                    <select
                      className="form-input"
                      value={selectedLevel}
                      onChange={(e) => {
                        const level = e.target.value;
                        setSelectedLevel(level);
                        setFormData({ ...formData, commonAreaId: '' });
                        if (level && formData.projectId) {
                          fetchCommonAreasByLevel(formData.projectId, level);
                        } else {
                          setCommonAreas([]);
                          setFilteredCommonAreas([]);
                        }
                      }}
                      disabled={!formData.projectId || loadingLevels}
                      required
                    >
                      <option value="">
                        {!formData.projectId ? 'Please select a project first' : loadingLevels ? 'Loading levels...' : 'Select a level'}
                      </option>
                      {levels.map((level) => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Area Dropdown */}
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Area *</label>
                    <select
                      className="form-input"
                      value={formData.commonAreaId || ''}
                      onChange={(e) => setFormData({ ...formData, commonAreaId: e.target.value })}
                      disabled={!selectedLevel || loadingCommonAreas}
                      required
                    >
                      <option value="">
                        {!selectedLevel ? 'Please select a level first' : loadingCommonAreas ? 'Loading areas...' : 'Select an area'}
                      </option>
                      {filteredCommonAreas.map((area) => (
                        <option key={area._id} value={area._id}>
                          {area.areaName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Inspection Type */}
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Inspection Type *</label>
                    <select
                      className="form-input"
                      value={formData.inspectionType}
                      onChange={(e) => setFormData({ ...formData, inspectionType: e.target.value })}
                    >
                      <option value="">Select Inspection Type</option>
                      {inspectionTypes.map((type) => (
                        <option key={type._id} value={type._id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Status and Impact Level row for Common Area */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Status *</label>
                    <select
                      className="form-input"
                      defaultValue="open"
                    >
                      <option value="open">Open</option>
                      <option value="assigned">Assigned</option>
                      <option value="accepted">Accepted</option>
                      <option value="not-accepted">Not Accepted</option>
                      <option value="completed">Completed</option>
                      <option value="closed">Closed</option>
                      <option value="reopened">Reopened</option>
                    </select>
                  </div>

                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Impact Level *</label>
                    <select
                      className="form-input"
                      value={formData.impactLevel}
                      onChange={(e) => setFormData({ ...formData, impactLevel: e.target.value })}
                    >
                      <option value="">Select Impact Level</option>
                      <option value="minor">Minor</option>
                      <option value="normal">Normal</option>
                      <option value="major">Major</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>

                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Location / Category *</label>
                    <select
                      className="form-input"
                      value={formData.locationCategory}
                      onChange={(e) => setFormData({ ...formData, locationCategory: e.target.value })}
                      disabled={!formData.projectId || loadingLocations}
                    >
                      <option value="">Select Location</option>
                      {locations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}

            {/* Impact Level, Location/Category, and Type of Deficiency row - for Units scope only */}
            {formData.scopeType === 'units' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Impact Level *</label>
                  <select
                    className="form-input"
                    value={formData.impactLevel}
                    onChange={(e) => setFormData({ ...formData, impactLevel: e.target.value })}
                  >
                    <option value="">Select Impact Level</option>
                    <option value="minor">Minor</option>
                    <option value="normal">Normal</option>
                    <option value="major">Major</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Location / Category *</label>
                  <select
                    className="form-input"
                    value={formData.locationCategory}
                    onChange={(e) => setFormData({ ...formData, locationCategory: e.target.value })}
                    disabled={!formData.projectId || loadingLocations}
                  >
                    <option value="">Select Location</option>
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Type of Deficiency *</label>
                  <select
                    className="form-input"
                    value={formData.typeOfDeficiency}
                    onChange={(e) => setFormData({ ...formData, typeOfDeficiency: e.target.value })}
                    disabled={!formData.projectId || loadingDeficiencyTypes}
                  >
                    <option value="">Select Type of Deficiency</option>
                    {deficiencyTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Type of Deficiency row - for Common Area scope only */}
            {formData.scopeType === 'commonArea' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Type of Deficiency *</label>
                  <select
                    className="form-input"
                    value={formData.typeOfDeficiency}
                    onChange={(e) => setFormData({ ...formData, typeOfDeficiency: e.target.value })}
                    disabled={!formData.projectId || loadingDeficiencyTypes}
                  >
                    <option value="">Select Type of Deficiency</option>
                    {deficiencyTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Priority *</label>
                  <select
                    className="form-input"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    disabled={!formData.projectId || loadingPriorities}
                  >
                    <option value="">Select Priority</option>
                    {priorities.map((priority) => (
                      <option key={priority._id} value={priority._id}>
                        {priority.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Due Date *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  />
                </div>
              </div>
            )}

            {/* Priority, Due Date, and Tags row - for Units scope only */}
            {formData.scopeType === 'units' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Priority *</label>
                  <select
                    className="form-input"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    disabled={!formData.projectId || loadingPriorities}
                  >
                    <option value="">Select Priority</option>
                    {priorities.map((priority) => (
                      <option key={priority._id} value={priority._id}>
                        {priority.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Due Date *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: 0, position: 'relative' }}>
                  <label className="form-label">Tags</label>
                  <div style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px',
                    minHeight: '38px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '6px',
                    alignItems: 'center',
                    cursor: 'text'
                  }}
                  onClick={() => document.getElementById('tag-input')?.focus()}
                  >
                    {selectedTags.map((tag, index) => (
                      <span
                        key={index}
                        style={{
                          backgroundColor: '#e0e0e0',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '14px'
                        }}
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const newTags = selectedTags.filter((_, i) => i !== index);
                            setSelectedTags(newTags);
                            setFormData({ ...formData, tags: newTags.join(',') });
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0',
                            fontSize: '16px',
                            color: '#666',
                            lineHeight: '1'
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    <input
                      id="tag-input"
                      type="text"
                      value={tagInput}
                      onChange={(e) => {
                        setTagInput(e.target.value);
                        setShowTagDropdown(true);
                      }}
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter' && tagInput.trim()) {
                          e.preventDefault();
                          const trimmedInput = tagInput.trim();

                          // Check if tag already exists in available tags
                          const existingTag = availableTags.find(
                            tag => tag.name.toLowerCase() === trimmedInput.toLowerCase()
                          );

                          if (existingTag && !selectedTags.includes(existingTag.name)) {
                            const newTags = [...selectedTags, existingTag.name];
                            setSelectedTags(newTags);
                            setFormData({ ...formData, tags: newTags.join(',') });
                            setTagInput('');
                            setShowTagDropdown(false);
                          } else if (!existingTag && !selectedTags.includes(trimmedInput)) {
                            // Add new tag to service settings
                            const newTag = await addNewTag(trimmedInput);
                            if (newTag) {
                              const newTags = [...selectedTags, newTag.name];
                              setSelectedTags(newTags);
                              setFormData({ ...formData, tags: newTags.join(',') });
                            }
                            setTagInput('');
                            setShowTagDropdown(false);
                          }
                        }
                      }}
                      onFocus={() => setShowTagDropdown(true)}
                      onBlur={() => setTimeout(() => setShowTagDropdown(false), 200)}
                      placeholder={selectedTags.length === 0 ? "Type to add tags" : ""}
                      style={{
                        border: 'none',
                        outline: 'none',
                        flex: 1,
                        minWidth: '120px',
                        fontSize: '14px',
                        padding: '4px'
                      }}
                    />
                  </div>

                  {/* Tag dropdown */}
                  {showTagDropdown && (filteredTags.length > 0 || (tagInput.trim() && !availableTags.find(t => t.name.toLowerCase() === tagInput.toLowerCase()))) && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      marginTop: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      {filteredTags.map((tag) => (
                        <div
                          key={tag._id}
                          onClick={() => {
                            if (!selectedTags.includes(tag.name)) {
                              const newTags = [...selectedTags, tag.name];
                              setSelectedTags(newTags);
                              setFormData({ ...formData, tags: newTags.join(',') });
                            }
                            setTagInput('');
                            setShowTagDropdown(false);
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          {tag.name}
                        </div>
                      ))}

                      {tagInput.trim() && !availableTags.find(t => t.name.toLowerCase() === tagInput.toLowerCase()) && (
                        <div
                          onClick={async () => {
                            const trimmedInput = tagInput.trim();
                            const newTag = await addNewTag(trimmedInput);
                            if (newTag) {
                              const newTags = [...selectedTags, newTag.name];
                              setSelectedTags(newTags);
                              setFormData({ ...formData, tags: newTags.join(',') });
                            }
                            setTagInput('');
                            setShowTagDropdown(false);
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            color: '#48182F',
                            fontWeight: 500
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          + Add "{tagInput.trim()}" as new tag
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tags row - for Common Area scope only */}
            {formData.scopeType === 'commonArea' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                <div className="form-group" style={{ marginBottom: 0, position: 'relative', gridColumn: 'span 3' }}>
                  <label className="form-label">Tags</label>
                  <div style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px',
                    minHeight: '38px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '6px',
                    alignItems: 'center',
                    cursor: 'text'
                  }}
                  onClick={() => document.getElementById('tag-input-ca')?.focus()}
                  >
                    {selectedTags.map((tag, index) => (
                      <span
                        key={index}
                        style={{
                          backgroundColor: '#e0e0e0',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: '14px'
                        }}
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const newTags = selectedTags.filter((_, i) => i !== index);
                            setSelectedTags(newTags);
                            setFormData({ ...formData, tags: newTags.join(',') });
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0',
                            fontSize: '16px',
                            color: '#666',
                            lineHeight: '1'
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    <input
                      id="tag-input-ca"
                      type="text"
                      value={tagInput}
                      onChange={(e) => {
                        setTagInput(e.target.value);
                        setShowTagDropdown(true);
                      }}
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter' && tagInput.trim()) {
                          e.preventDefault();
                          const trimmedInput = tagInput.trim();

                          // Check if tag already exists in available tags
                          const existingTag = availableTags.find(
                            tag => tag.name.toLowerCase() === trimmedInput.toLowerCase()
                          );

                          if (existingTag && !selectedTags.includes(existingTag.name)) {
                            const newTags = [...selectedTags, existingTag.name];
                            setSelectedTags(newTags);
                            setFormData({ ...formData, tags: newTags.join(',') });
                            setTagInput('');
                            setShowTagDropdown(false);
                          } else if (!existingTag && !selectedTags.includes(trimmedInput)) {
                            // Add new tag to service settings
                            const newTag = await addNewTag(trimmedInput);
                            if (newTag) {
                              const newTags = [...selectedTags, newTag.name];
                              setSelectedTags(newTags);
                              setFormData({ ...formData, tags: newTags.join(',') });
                            }
                            setTagInput('');
                            setShowTagDropdown(false);
                          }
                        }
                      }}
                      onFocus={() => setShowTagDropdown(true)}
                      onBlur={() => setTimeout(() => setShowTagDropdown(false), 200)}
                      placeholder={selectedTags.length === 0 ? "Type to add tags" : ""}
                      style={{
                        border: 'none',
                        outline: 'none',
                        flex: 1,
                        minWidth: '120px',
                        fontSize: '14px',
                        padding: '4px'
                      }}
                    />
                  </div>

                  {/* Tag dropdown */}
                  {showTagDropdown && (filteredTags.length > 0 || (tagInput.trim() && !availableTags.find(t => t.name.toLowerCase() === tagInput.toLowerCase()))) && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      marginTop: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      {filteredTags.map((tag) => (
                        <div
                          key={tag._id}
                          onClick={() => {
                            if (!selectedTags.includes(tag.name)) {
                              const newTags = [...selectedTags, tag.name];
                              setSelectedTags(newTags);
                              setFormData({ ...formData, tags: newTags.join(',') });
                            }
                            setTagInput('');
                            setShowTagDropdown(false);
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          {tag.name}
                        </div>
                      ))}

                      {tagInput.trim() && !availableTags.find(t => t.name.toLowerCase() === tagInput.toLowerCase()) && (
                        <div
                          onClick={async () => {
                            const trimmedInput = tagInput.trim();
                            const newTag = await addNewTag(trimmedInput);
                            if (newTag) {
                              const newTags = [...selectedTags, newTag.name];
                              setSelectedTags(newTags);
                              setFormData({ ...formData, tags: newTags.join(',') });
                            }
                            setTagInput('');
                            setShowTagDropdown(false);
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            color: '#48182F',
                            fontWeight: 500
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          + Add "{tagInput.trim()}" as new tag
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Title */}
            <div className="form-group" style={{ marginBottom: '10px' }}>
              <label className="form-label">Title *</label>
              <input
                type="text"
                className="form-input"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter title"
              />
            </div>

            {/* Description */}
            <div className="form-group" style={{ marginBottom: '10px' }}>
              <label className="form-label">Description</label>
              <textarea
                className="form-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter description"
                rows={4}
                style={{ minHeight: '90px' }}
              />
            </div>

            {/* Attachments */}
            <div className="form-group" style={{ marginBottom: '10px' }}>
              <label className="form-label">Attachments</label>
              <div style={{
                border: `2px dashed ${isDraggingOver ? '#6B46C1' : '#D1D5DB'}`,
                borderRadius: '6px',
                padding: '20px',
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDraggingOver ? '#F3F0FF' : '#F9FAFB',
                transition: 'border-color 0.2s, background-color 0.2s'
              }}
              onClick={() => document.getElementById('file-upload')?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
              onDragLeave={() => setIsDraggingOver(false)}
              onDrop={handleDrop}
              >
                <Upload size={28} color={isDraggingOver ? '#6B46C1' : '#9CA3AF'} style={{ margin: '0 auto 8px' }} />
                <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>
                  {isDraggingOver ? 'Drop image here' : 'Click or drag & drop an image'}
                </p>
                <p style={{ fontSize: '11px', color: '#9CA3AF' }}>
                  You can mark or annotate images before adding
                </p>
                <input
                  id="file-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{ marginTop: '8px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    document.getElementById('file-upload')?.click();
                  }}
                >
                  Add
                </button>
              </div>
              {/* Attachment Previews */}
              {formData.attachments.length > 0 && (
                <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {attachmentPreviews.map((preview, index) => (
                    <div key={index} style={{
                      position: 'relative',
                      width: '100px',
                      height: '100px',
                      borderRadius: '6px',
                      overflow: 'hidden',
                      border: '1px solid #E5E7EB'
                    }}>
                      <img
                        src={preview}
                        alt={formData.attachments[index].name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        style={{
                          position: 'absolute',
                          top: '4px',
                          right: '4px',
                          background: 'rgba(239, 68, 68, 0.9)',
                          border: 'none',
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          cursor: 'pointer',
                          padding: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white'
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Additional Information Section */}
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#6B7280',
              marginTop: '16px',
              marginBottom: '10px',
              paddingBottom: '6px',
              borderBottom: '1px solid #E5E7EB'
            }}>
              Additional Information
            </div>

            {/* Private Checkbox */}
            <div className="form-group" style={{ marginBottom: '10px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.isPrivate}
                  onChange={(e) => setFormData({ ...formData, isPrivate: e.target.checked })}
                />
                <span style={{ fontSize: '14px' }}>Private</span>
              </label>
            </div>

            {/* Schedule Impact, Cost Impact, and Chargeback in same row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Schedule Impact</label>
                <select
                  className="form-input"
                  value={formData.scheduleImpact}
                  onChange={(e) => setFormData({ ...formData, scheduleImpact: e.target.value })}
                >
                  <option value="">Select Schedule Impact</option>
                  <option value="yes">Yes</option>
                  <option value="yes-unknown">Yes (Unknown)</option>
                  <option value="no">No</option>
                  <option value="not-sure">Not Sure</option>
                  <option value="n/a">N/A</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Cost Impact</label>
                <select
                  className="form-input"
                  value={formData.costImpact}
                  onChange={(e) => setFormData({ ...formData, costImpact: e.target.value })}
                >
                  <option value="">Select Cost Impact</option>
                  <option value="yes">Yes</option>
                  <option value="yes-unknown">Yes (Unknown)</option>
                  <option value="no">No</option>
                  <option value="not-sure">Not Sure</option>
                  <option value="n/a">N/A</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Chargeback</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.chargeback}
                  onChange={(e) => setFormData({ ...formData, chargeback: e.target.value })}
                  placeholder="Enter chargeback"
                />
              </div>
            </div>

            {/* Trade, Approver, and Cost Codes in same row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Trade *</label>
                <select
                  className="form-input"
                  value={formData.trade}
                  onChange={(e) => setFormData({ ...formData, trade: e.target.value })}
                  disabled={!formData.projectId || loadingTrades}
                >
                  <option value="">Select Trade</option>
                  {trades.map((trade) => (
                    <option key={trade._id} value={trade._id}>
                      {trade.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Approver</label>
                <select
                  className="form-input"
                  value={formData.approver}
                  onChange={(e) => setFormData({ ...formData, approver: e.target.value })}
                  disabled={!formData.projectId || loadingManagers}
                >
                  <option value="">Select Approver</option>
                  {managers.map((manager) => (
                    <option key={manager.contactId} value={manager.contactId}>
                      {manager.name || manager.email}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Cost Codes</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.costCodes}
                  onChange={(e) => setFormData({ ...formData, costCodes: e.target.value })}
                  placeholder="Enter cost codes"
                />
              </div>
            </div>

            {/* Private Notes */}
            <div className="form-group" style={{ marginBottom: '10px' }}>
              <label className="form-label">Private Notes</label>
              <textarea
                className="form-input"
                value={formData.privateNotes}
                onChange={(e) => setFormData({ ...formData, privateNotes: e.target.value })}
                placeholder="Enter private notes"
                rows={3}
              />
            </div>

            {/* Related Deficiency Items Section */}
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#6B7280',
              marginTop: '16px',
              marginBottom: '10px',
              paddingBottom: '6px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>Related Deficiency Items</span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowRelatedDeficiencyModal(true)}
                disabled={!formData.projectId}
              >
                Add
              </button>
            </div>

            {/* Display related items */}
            {formData.relatedDeficiencyItems.length === 0 ? (
              <div style={{
                padding: '20px',
                textAlign: 'center',
                color: '#9CA3AF',
                fontSize: '14px',
                backgroundColor: '#F9FAFB',
                borderRadius: '6px'
              }}>
                No related deficiency items added
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {formData.relatedDeficiencyItems.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '12px',
                      backgroundColor: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span style={{ fontSize: '14px', color: '#48182F', fontWeight: '500' }}>
                        {item.deficiencyNumber ? `#${item.deficiencyNumber}` : ''} {item.title || 'Deficiency'}
                      </span>
                      <span style={{ fontSize: '12px', color: '#6B7280' }}>
                        {item.relationshipType === 'duplicate' ? 'Duplicate' : 'Related'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveRelatedDeficiency(index)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#EF4444'
                      }}
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Drawer Footer */}
        <div className="drawer-footer">
          <button
            onClick={onClose}
            className="btn btn-secondary btn-md"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="btn btn-primary btn-md"
            disabled={saving}
          >
            {saving ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>

      {/* Image Editor Modal */}
      {currentImageFile && (
        <ImageEditor
          isOpen={imageEditorOpen}
          onClose={() => {
            setImageEditorOpen(false);
            setCurrentImageFile(null);
          }}
          imageFile={currentImageFile}
          onSave={handleImageEditorSave}
        />
      )}

      {/* Related Deficiency Modal */}
      <RelatedDeficiencyModal
        isOpen={showRelatedDeficiencyModal}
        onClose={() => setShowRelatedDeficiencyModal(false)}
        onSave={handleSaveRelatedDeficiencies}
        workspaceId={workspaceId}
        projectId={formData.projectId}
        unitId={formData.unitId}
        existingRelatedIds={formData.relatedDeficiencyItems.map(item => item.deficiency)}
      />
    </>
  );
};

export default DeficiencyFormSlider;
